import { useEffect, useRef } from "react";

/**
 * Page-level keyboard shortcuts for the review queues (j/k navigation, action
 * letters, digits for ordinal choices, `?` for the cheat sheet).
 *
 * A key spec is `"j"`, `"ArrowDown"`, `"?"`, `"["`, `"1"`, `"Enter"`, or with
 * modifiers `"mod+Enter"` / `"shift+s"` (`mod` = ⌘ on Mac, Ctrl elsewhere).
 *
 * Shortcuts are ignored while the admin is typing (input / textarea / select /
 * contenteditable), while focus is inside an open Radix layer (dialogs, alert
 * dialogs, popper content such as Select / DropdownMenu — those own their own
 * keys), and when an unrequested modifier is held (so ⌘K and the header's `/`
 * search keep working). Held-key repeats are dropped for action keys.
 */
export interface Shortcut {
  keys: string | string[];
  label: string;
  group?: "Navigate" | "Actions" | "General";
  handler: (event: KeyboardEvent) => void;
  /** Rendered greyed in the cheat sheet and ignored when false. */
  enabled?: boolean;
  /** j/k style keys may auto-repeat; actions must not. */
  allowRepeat?: boolean;
  /** Fire even while an input has focus (rare — e.g. Escape). */
  allowInInput?: boolean;
}

export const isMac =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform);

// Only OPEN layers own the keyboard: Radix keeps a closing dialog mounted
// (data-state="closed") until its exit animation ends, and focus can still sit
// inside it for a beat — a shortcut pressed then must not be swallowed.
const LAYER_SELECTOR =
  '[role="dialog"]:not([data-state="closed"]), [role="alertdialog"]:not([data-state="closed"]), [data-radix-popper-content-wrapper]';

const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
};

const isInsideLayer = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest(LAYER_SELECTOR) !== null;

interface ParsedSpec {
  key: string;
  mod: boolean;
  shift: boolean;
  alt: boolean;
}

const parseSpec = (spec: string): ParsedSpec => {
  const parts = spec.split("+");
  const key = parts.pop() ?? "";
  const modifiers = parts.map((part) => part.toLowerCase());
  return {
    key,
    mod: modifiers.includes("mod"),
    shift: modifiers.includes("shift"),
    alt: modifiers.includes("alt"),
  };
};

/** Does this keydown match the spec? Letters compare case-insensitively. */
export const matchShortcut = (
  event: KeyboardEvent,
  keys: string | string[],
): boolean => {
  const specs = Array.isArray(keys) ? keys : [keys];
  return specs.some((raw) => {
    const spec = parseSpec(raw);
    const modHeld = isMac ? event.metaKey : event.ctrlKey;
    const otherModHeld = isMac ? event.ctrlKey : event.metaKey;
    if (spec.mod !== modHeld || otherModHeld) return false;
    if (spec.alt !== event.altKey) return false;
    const isLetter = /^[a-z]$/i.test(spec.key);
    if (isLetter) {
      if (spec.shift !== event.shiftKey) return false;
      return event.key.toLowerCase() === spec.key.toLowerCase();
    }
    // Symbols like "?" / "[" / digits: the browser already resolved shift.
    return event.key === spec.key;
  });
};

const KEY_LABELS: Record<string, string> = {
  ArrowDown: "↓",
  ArrowUp: "↑",
  ArrowLeft: "←",
  ArrowRight: "→",
  Enter: "↵",
  Escape: "Esc",
  " ": "Space",
};

/** "ArrowDown" → "↓", "mod+Enter" → "⌘↵" / "Ctrl ↵", "j" → "J". */
export const formatKeyLabel = (spec: string): string => {
  const parsed = parseSpec(spec);
  const base = KEY_LABELS[parsed.key] ?? parsed.key.toUpperCase();
  const parts: string[] = [];
  if (parsed.mod) parts.push(isMac ? "⌘" : "Ctrl");
  if (parsed.alt) parts.push(isMac ? "⌥" : "Alt");
  if (parsed.shift) parts.push("⇧");
  parts.push(base);
  return parts.join(isMac ? "" : " ");
};

export function useKeyboardShortcuts(
  shortcuts: Shortcut[],
  options: { enabled?: boolean } = {},
): void {
  const enabled = options.enabled ?? true;
  // Latest shortcuts without re-binding the listener on every render.
  const shortcutsRef = useRef(shortcuts);
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  });

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const typing = isTypingTarget(event.target);
      if (isInsideLayer(event.target)) return;
      for (const shortcut of shortcutsRef.current) {
        if (shortcut.enabled === false) continue;
        if (typing && !shortcut.allowInInput) continue;
        if (event.repeat && !shortcut.allowRepeat) continue;
        if (!matchShortcut(event, shortcut.keys)) continue;
        event.preventDefault();
        shortcut.handler(event);
        return;
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
}
